import { toastInfo } from 'util/toast';

/**
 * Native (OS/Chrome) desktop notifications with a graceful in-app toast
 * fallback. Used for live chat messages so they surface like a real chat app —
 * even when the browser tab is in the background — instead of an in-app-only
 * toast that you'd never see unless you were already looking at the tab.
 */

let permissionAsked = false;

/**
 * Ask for notification permission once, lazily. Safe to call on mount and on a
 * user gesture; the browser only shows the prompt the first time. No-ops when
 * the API is unavailable or a decision was already made.
 */
export function ensureNotifyPermission() {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'default') return;
  if (permissionAsked) return;
  permissionAsked = true;
  try {
    Notification.requestPermission().catch(() => {});
  } catch {
    /* older browsers throw on the promise form — ignore */
  }
}

/** Page-level ("non-persistent") notification. Returns false if the browser
 *  refused, so the caller can fall back. */
function notifyFromPage(title, { body, onClick, tag }) {
  try {
    const note = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag,
      renotify: Boolean(tag),
    });
    note.onclick = () => {
      window.focus();
      onClick?.();
      note.close();
    };
    return true;
  } catch {
    return false;
  }
}

/**
 * Show a desktop notification. Falls back to an in-app toast when permission
 * isn't granted or the browser can't show one. `onClick` fires when either the
 * OS notification or the toast is clicked.
 *
 * The same event also arrives as an FCM push, and `tag` is what stops the two
 * from popping twice — but only within one namespace: Chrome tracks page-shown
 * ("non-persistent") and service-worker-shown ("persistent") notifications
 * separately, so an identical tag across the two does NOT collapse them. The
 * FCM copy is shown by the page while this tab is visible and by the service
 * worker while it isn't, so we have to match that split. `linkUrl` is what the
 * SW-shown copy navigates to on click (it can't carry a JS callback).
 */
export function notifyDesktop(title, { body = '', onClick, tag, linkUrl } = {}) {
  const canNotify =
    'Notification' in window && Notification.permission === 'granted';

  if (canNotify) {
    const hidden = document.visibilityState !== 'visible';
    if (hidden && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistration()
        .then((reg) => {
          if (!reg) throw new Error('no service worker registered');
          return reg.showNotification(title, {
            body,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag,
            renotify: Boolean(tag),
            data: linkUrl ? { link_url: linkUrl } : {},
          });
        })
        .catch(() => {
          // No SW (push disabled in dev) — a page notification is better than
          // silence, and with nothing from FCM there is nothing to duplicate.
          if (!notifyFromPage(title, { body, onClick, tag })) {
            toastInfo(`${title}${body ? `: ${body}` : ''}`, onClick ? { onClick } : undefined);
          }
        });
      return;
    }
    if (notifyFromPage(title, { body, onClick, tag })) return;
  }

  toastInfo(`${title}${body ? `: ${body}` : ''}`, onClick ? { onClick } : undefined);
}
